export default {
  id: 'act-scoring',
  title: 'ACT Scoring Explained',
  content: `## ACT Scoring Explained

Every ACT trade plan receives a score from **0 to 100** across four dimensions. The overall score is a weighted composite of all four.

> A score of 80 does not mean you made money. It means you made a disciplined, well-reasoned decision. In the long run, a good process produces better outcomes — but no single trade guarantees a result.

---

## The Four Dimensions

---

### 1. Setup Quality (0–100)

Measures whether the signal was valid for the current market context at the time of the setup.

**Factors:**
- Is the scanner signal appropriate for the trend direction? (A bullish breakout signal in a DOWNTREND scores lower)
- Is RSI in a supportive range? (Entering a breakout when RSI is already at 78 scores lower — momentum is extended)
- Is volume supporting the signal? (Breakout with 0.8× average volume scores lower — no conviction)
- Is the signal historically reliable in this configuration?

**How to improve:** Only Enter on setups where the signal, trend, RSI, and volume are all aligned. Signals with one factor against them are lower quality.

---

### 2. Entry Quality (0–100)

Measures whether your planned entry price was sensible relative to the signal.

**Factors:**
- How far is your planned entry from the signal price? (Entry far above signal = chasing)
- Is the entry level consistent with nearby support/resistance visible on the chart?
- Are you entering near a key reference (SMA20, VWAP, prior high) or away from it?

**How to improve:** Enter at or near the signal price, not after the stock has already moved significantly from it.

---

### 3. Risk Management (0–100)

Measures the quality of your risk parameters.

**Factors:**

| Check | Scoring impact |
|---|---|
| R:R ≥ 2.0 | Full score on this factor |
| R:R 1.5–2.0 | Partial credit |
| R:R < 1.0 | Significant penalty |
| Stop ≥ 1× ATR away from entry | Full score on stop quality |
| Stop < 0.5× ATR | Flagged — likely to be hit by normal noise |
| No stop loss set | Zero on the risk management dimension |
| Position size within risk profile limit | Full score |
| Position size significantly over limit | Penalised |

**How to improve:** Always set a stop. Place it at a level that gives the trade room to breathe (at least 1× ATR). Ensure your target is at least 2× the stop distance.

---

### 4. Decision Quality (0–100)

Measures whether your Enter / Skip / Wait decision matched the correct answer in hindsight.

**Correct decision:**
- Base score of 75–100, scaled by how clear-cut the correct answer was
- A strong outcome (clear target hit or clear stop hit) gives a higher score

**Incorrect decision:**
- Base score of 20–50
- Partial credit is given for a well-reasoned, well-structured plan even on the wrong decision
- Choosing SKIP on a scenario where the outcome was a Stop Loss hit is rewarded, even if the "market answer" was Enter

**How to improve:** Focus on the quality of your analysis — not on trying to guess the correct answer. A well-reasoned wrong decision scores better than an unthinking right one.

---

## Overall Score

**Overall = (Setup Quality × 0.25) + (Entry Quality × 0.20) + (Risk Management × 0.30) + (Decision Quality × 0.25)**

Risk Management carries the highest weight because it is the dimension most within your control and most predictive of long-term survival.

---

## Score Benchmarks

| Score | What it means |
|---|---|
| 90–100 | Exceptional — all four dimensions well-executed |
| 80–89 | Strong — the habit you should aim to make consistent |
| 70–79 | Adequate — identifiable gaps in one or two dimensions |
| 60–69 | Weak — significant gaps in plan quality or risk management |
| Below 60 | Poor — revisit fundamentals of risk management and setup reading |

Aim for **80+ consistently across 30+ sessions** before considering your decision quality well-calibrated.`,
};
